import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import useTableHiddenColumns, {
  TableHiddenColumnsProvider,
} from '@resources/js/contexts/useTableHiddenColumns';
import useLocalStorage from '@resources/js/hooks/useLocalStorage';

const hideableColumns = ['hits', 'averageHit', 'critPercent', 'missPercent'];

export type ThroughputBreakdownHiddenColumnsContext = {
  showAllColumns: boolean;
  setShowAllColumns: Dispatch<SetStateAction<boolean>>;
};

const defaultHiddenColumns = {
  showAllColumns: false,
  setShowAllColumns: () => undefined,
};

export const ThroughputBreakdownHiddenColumnsContext =
  createContext<ThroughputBreakdownHiddenColumnsContext>(defaultHiddenColumns);

export const ThroughputBreakdownHiddenColumnsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  return (
    <TableHiddenColumnsProvider initialValue={hideableColumns}>
      <ThroughputBreakdownHiddenColumnsProviderInner>
        {children}
      </ThroughputBreakdownHiddenColumnsProviderInner>
    </TableHiddenColumnsProvider>
  );
};

const ThroughputBreakdownHiddenColumnsProviderInner = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { setHiddenColumns } = useTableHiddenColumns();
  const [showAllColumns, setShowAllColumns] = useLocalStorage(
    'BuildsThroughputSectionShowAllColumns',
    false
  );
  useEffect(() => {
    setHiddenColumns(showAllColumns ? [] : hideableColumns);
  }, [setHiddenColumns, showAllColumns]);

  return (
    <ThroughputBreakdownHiddenColumnsContext.Provider
      value={{ showAllColumns, setShowAllColumns }}
    >
      {children}
    </ThroughputBreakdownHiddenColumnsContext.Provider>
  );
};

const useThroughputBreakdownHiddenColumns: () => ThroughputBreakdownHiddenColumnsContext =
  () =>
    useContext(ThroughputBreakdownHiddenColumnsContext) ?? defaultHiddenColumns;

export default useThroughputBreakdownHiddenColumns;
